import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  AsYouType,
  type CountryCode
} from 'libphonenumber-js';
import { Search, ChevronDown, Check, Phone, Globe, X } from 'lucide-react';

// Generate flag emoji from 2-letter ISO country code
export function getCountryFlag(countryCode?: string): string {
  if (!countryCode || countryCode.length !== 2) return '🌐';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// Popular / Priority countries for quick access
const PRIORITY_COUNTRIES: { code: CountryCode; name: string; callingCode: string }[] = [
  { code: 'BR', name: 'Brasil', callingCode: '+55' },
  { code: 'JP', name: 'Japão', callingCode: '+81' },
  { code: 'US', name: 'Estados Unidos', callingCode: '+1' },
  { code: 'PT', name: 'Portugal', callingCode: '+351' },
  { code: 'ES', name: 'Espanha', callingCode: '+34' },
  { code: 'IT', name: 'Itália', callingCode: '+39' },
  { code: 'GB', name: 'Reino Unido', callingCode: '+44' },
  { code: 'DE', name: 'Alemanha', callingCode: '+49' },
  { code: 'FR', name: 'França', callingCode: '+33' },
  { code: 'CA', name: 'Canadá', callingCode: '+1' },
  { code: 'AU', name: 'Austrália', callingCode: '+61' },
  { code: 'PY', name: 'Paraguai', callingCode: '+595' },
  { code: 'UY', name: 'Uruguai', callingCode: '+598' },
  { code: 'AR', name: 'Argentina', callingCode: '+54' },
  { code: 'CL', name: 'Chile', callingCode: '+56' },
];

export interface CountryPhoneInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showValidationHint?: boolean;
}

export const CountryPhoneInput: React.FC<CountryPhoneInputProps> = ({
  id,
  value = '',
  onChange,
  defaultCountry = 'BR',
  placeholder,
  className = '',
  inputClassName = '',
  required = false,
  disabled = false,
  autoFocus = false,
  size = 'md',
  showValidationHint = false,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    (defaultCountry?.toUpperCase() as CountryCode) || 'BR'
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Formatter for localized country names
  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames(['pt-BR', 'pt'], { type: 'region' });
    } catch {
      return { of: (code: string) => code };
    }
  }, []);

  // Build full country list with localized names in Portuguese
  const allCountryList = useMemo(() => {
    const countries = getCountries();
    return countries
      .map((c) => {
        let calling = '';
        try {
          calling = `+${getCountryCallingCode(c)}`;
        } catch {
          calling = '';
        }
        const ptName = regionNames.of(c) || c;
        return {
          code: c,
          name: ptName,
          flag: getCountryFlag(c),
          callingCode: calling,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [regionNames]);

  // Current active calling code (e.g. +55)
  const currentCallingCode = useMemo(() => {
    try {
      return `+${getCountryCallingCode(selectedCountry)}`;
    } catch {
      return '+55';
    }
  }, [selectedCountry]);

  // Extract national part from external value
  const displayNationalValue = useMemo(() => {
    if (!value) return '';
    const clean = value.trim();

    // If starts with +, parse country and format
    if (clean.startsWith('+')) {
      try {
        const parsed = parsePhoneNumberFromString(clean);
        if (parsed) {
          const ayt = new AsYouType(parsed.country || selectedCountry);
          return ayt.input(parsed.nationalNumber);
        }
      } catch {
        // ignore
      }

      // If starts with current calling code
      if (clean.startsWith(currentCallingCode)) {
        const remaining = clean.slice(currentCallingCode.length).trim();
        const ayt = new AsYouType(selectedCountry);
        return ayt.input(remaining.replace(/\D/g, ''));
      }
    }

    // Format raw digits according to current country
    const digits = clean.replace(/\D/g, '');
    const ayt = new AsYouType(selectedCountry);
    return ayt.input(digits);
  }, [value, currentCallingCode, selectedCountry]);

  // Synchronize country from value if value starts with a known international prefix
  useEffect(() => {
    if (value && value.trim().startsWith('+')) {
      try {
        const parsed = parsePhoneNumberFromString(value.trim());
        if (parsed?.country && parsed.country !== selectedCountry) {
          setSelectedCountry(parsed.country);
        }
      } catch {
        // ignore
      }
    }
  }, [value, selectedCountry]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Filtered country list based on user search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return allCountryList;
    const q = searchQuery.toLowerCase().trim();
    return allCountryList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.callingCode.includes(q)
    );
  }, [allCountryList, searchQuery]);

  // Current active country info
  const activeCountryInfo = useMemo(() => {
    const name = regionNames.of(selectedCountry) || selectedCountry;
    return {
      code: selectedCountry,
      name,
      flag: getCountryFlag(selectedCountry),
      callingCode: currentCallingCode,
    };
  }, [selectedCountry, regionNames, currentCallingCode]);

  // Handle selecting a country from the dropdown
  const handleSelectCountry = useCallback((countryCode: CountryCode) => {
    setSelectedCountry(countryCode);
    setIsDropdownOpen(false);
    setSearchQuery('');

    try {
      const newCallingCode = `+${getCountryCallingCode(countryCode)}`;
      const digits = value ? value.replace(/\D/g, '') : '';
      
      if (!digits) {
        onChange(newCallingCode);
      } else {
        // Check if old value had previous calling code
        const prevCallingDigits = currentCallingCode.replace(/\D/g, '');
        let pureDigits = digits;
        if (digits.startsWith(prevCallingDigits)) {
          pureDigits = digits.slice(prevCallingDigits.length);
        }
        if (pureDigits.length > 0) {
          const ayt = new AsYouType(countryCode);
          const formattedNat = ayt.input(pureDigits);
          onChange(`${newCallingCode} ${formattedNat}`);
        } else {
          onChange(newCallingCode);
        }
      }
    } catch {
      // ignore
    }
  }, [value, currentCallingCode, onChange]);

  // Handle typing inside the input field
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;
    const digitsOnly = rawInput.replace(/\D/g, '');

    if (!digitsOnly) {
      onChange('');
      return;
    }

    // Format national digits with AsYouType
    const ayt = new AsYouType(selectedCountry);
    const formattedNational = ayt.input(digitsOnly);

    // Provide complete international number to parent
    const fullIntl = `${currentCallingCode} ${formattedNational}`;
    onChange(fullIntl);
  };

  // Button sizes
  const triggerPadding = {
    sm: 'py-1.5 px-2.5 text-xs',
    md: 'py-2 px-3 text-xs',
    lg: 'py-2.5 px-3.5 text-sm',
  }[size];

  // Input sizes
  const inputPadding = {
    sm: 'py-1.5 px-3 text-xs',
    md: 'py-2 px-3 text-xs',
    lg: 'py-2.5 px-3.5 text-sm',
  }[size];

  // Check validity if value exists
  const isValid = useMemo(() => {
    if (!value || value.length < 6) return null;
    try {
      const parsed = parsePhoneNumberFromString(value.trim(), selectedCountry);
      return parsed ? parsed.isValid() || parsed.isPossible() : null;
    } catch {
      return null;
    }
  }, [value, selectedCountry]);

  return (
    <div className={`relative w-full ${className}`} id={id ? `${id}-wrapper` : undefined}>
      <div className="flex items-center gap-1.5 w-full">
        {/* Country Selector Trigger Button with Flag and Dial Code */}
        <div className="relative shrink-0" ref={dropdownRef}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`flex items-center gap-1.5 bg-slate-50 hover:bg-slate-100 active:bg-slate-200 border border-slate-300 rounded-xl transition-all cursor-pointer select-none font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xs ${triggerPadding}`}
            title={`País: ${activeCountryInfo.name} (${activeCountryInfo.callingCode}) - Clique para alterar`}
            aria-label="Selecionar país do telefone"
          >
            <span className="text-base leading-none select-none" role="img" aria-label={activeCountryInfo.name}>
              {activeCountryInfo.flag}
            </span>
            <span className="font-mono font-bold text-slate-700 text-[11px]">
              {activeCountryInfo.callingCode}
            </span>
            <ChevronDown
              className={`w-3 h-3 text-slate-400 transition-transform duration-150 ${
                isDropdownOpen ? 'rotate-180 text-emerald-600' : ''
              }`}
            />
          </button>

          {/* Searchable Country Picker Dropdown Popover */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1.5 w-72 sm:w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in-overlay">
              {/* Header with Search */}
              <div className="p-2.5 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center justify-between mb-1.5 px-0.5">
                  <span className="text-[11px] font-black uppercase text-slate-600 flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5 text-emerald-600" />
                    Selecione o País
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(false)}
                    className="text-slate-400 hover:text-slate-600 p-0.5 rounded-md cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar país ou código (ex: Brasil, Japão, +55)..."
                    className="w-full pl-8 pr-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Quick Priority Countries Chips */}
              {!searchQuery && (
                <div className="p-2 bg-slate-50/50 border-b border-slate-100">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 px-1">
                    Mais Frequentes:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {PRIORITY_COUNTRIES.map((pc) => (
                      <button
                        key={pc.code}
                        type="button"
                        onClick={() => handleSelectCountry(pc.code)}
                        className={`px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-all cursor-pointer ${
                          selectedCountry === pc.code
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'bg-white hover:bg-slate-200/80 text-slate-700 border border-slate-200'
                        }`}
                      >
                        <span>{getCountryFlag(pc.code)}</span>
                        <span>{pc.name}</span>
                        <span className="font-mono text-[10px] opacity-75">{pc.callingCode}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Scrollable Country List */}
              <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-1">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => {
                    const isSelected = selectedCountry === country.code;
                    return (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => handleSelectCountry(country.code)}
                        className={`w-full px-2.5 py-2 text-left flex items-center justify-between rounded-xl transition-colors text-xs cursor-pointer ${
                          isSelected
                            ? 'bg-emerald-50 text-emerald-950 font-bold'
                            : 'hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-lg leading-none shrink-0 select-none">
                            {country.flag}
                          </span>
                          <span className="truncate">{country.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          <span className="font-mono text-[11px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-md">
                            {country.callingCode}
                          </span>
                          {isSelected && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-xs text-slate-400">
                    Nenhum país encontrado para "{searchQuery}".
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* National Formatted Number Input */}
        <div className="relative flex-1">
          <input
            id={id}
            type="tel"
            value={displayNationalValue}
            onChange={handleInputChange}
            placeholder={
              placeholder ||
              (selectedCountry === 'BR'
                ? '(11) 99999-9999'
                : selectedCountry === 'JP'
                ? '090-1234-5678'
                : selectedCountry === 'US'
                ? '(555) 123-4567'
                : selectedCountry === 'PT'
                ? '912 345 678'
                : 'DDD + Número')
            }
            disabled={disabled}
            autoFocus={autoFocus}
            required={required}
            className={`w-full bg-white border border-slate-300 rounded-xl font-mono text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-2xs transition-all ${inputPadding} ${inputClassName}`}
          />
        </div>
      </div>

      {/* Validation / Format Helper feedback */}
      {showValidationHint && value && (
        <div className="mt-1 text-[10px] flex items-center gap-1 font-medium">
          {isValid ? (
            <span className="text-emerald-600 flex items-center gap-1">
              <Check className="w-3 h-3 text-emerald-600 shrink-0" />
              Número válido conforme padrão internacional ({activeCountryInfo.name})
            </span>
          ) : (
            <span className="text-amber-600 flex items-center gap-1">
              <Phone className="w-3 h-3 text-amber-500 shrink-0" />
              Digite o número completo com DDD ({activeCountryInfo.name})
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CountryPhoneInput;
